/**
 * Shared task metadata chip components.
 *
 * Canonical color maps and reusable primitives for displaying task
 * metadata consistently across kanban cards, list rows, calendar pills,
 * right panel, and sidebar.
 */

import { cn } from "@/lib/cn";
import { projectColorForDisplay } from "@/lib/home-project-colors";
import { Icons } from "@/components/ui/icons";
import type { TaskStatus, Task } from "@/api/tasks";
import { formatDate, useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import type en from "@/i18n/messages/frames-taskboard/en";
import { useProjectStore } from "@/stores/project-store";
import { useBoardShowsProjects } from "./board-projects-context";

/* ── Canonical color maps ──────────────────────────────────── */

export const STATUS_DOT_COLORS: Record<TaskStatus, string> = {
  backlog: "var(--color-muted)",
  todo: "var(--color-status-selected)",
  in_progress: "var(--color-warning)",
  blocked: "var(--color-status-error)",
  // No dedicated status-review token; --accent-purple is the nearest semantic match.
  in_review: "var(--accent-purple)",
  done: "var(--color-accent)",
};

export const PRIORITY_BORDER_CLASSES: Record<string, string> = {
  urgent: "border-l-danger",
  high: "border-l-warning",
  medium: "border-l-status-selected",
  low: "border-l-muted",
};

export const PRIORITY_TEXT_COLORS: Record<string, string> = {
  urgent: "text-danger",
  high: "text-warning",
  medium: "text-status-selected",
  low: "text-muted",
};

/** Catalogue keys of the status names in `framesTaskboardMessages`. */
export const TASK_STATUS_LABEL_KEYS = {
  backlog: "statusBacklog",
  todo: "statusTodo",
  in_progress: "statusInProgress",
  blocked: "statusBlocked",
  in_review: "statusInReview",
  done: "statusDone",
} as const satisfies Record<TaskStatus, keyof typeof en>;

/** Catalogue keys of the priority names in `framesTaskboardMessages`. */
export const TASK_PRIORITY_LABEL_KEYS = {
  urgent: "priorityUrgent",
  high: "priorityHigh",
  medium: "priorityMedium",
  low: "priorityLow",
} as const satisfies Record<string, keyof typeof en>;

/* ── Chip components ───────────────────────────────────────── */

/**
 * Colored dot indicating task status with spec-defined hex colors.
 *
 * Distinct from the compact-surface StatusDot in @/components/ui.
 * This component uses the explicit hex colors from the taskboard views spec.
 */
export function TaskStatusDot({
  status,
  size = 7,
  className,
}: {
  status: TaskStatus;
  size?: number;
  className?: string;
}) {
  const t = useT(framesTaskboardMessages);
  return (
    <span
      className={cn("shrink-0 rounded-full", className)}
      style={{
        width: size,
        height: size,
        backgroundColor: STATUS_DOT_COLORS[status],
      }}
      aria-label={t(TASK_STATUS_LABEL_KEYS[status])}
      data-testid="status-dot"
    />
  );
}

/** Wrapper that adds a left border colored by priority. */
export function PriorityBorder({
  priority,
  className,
  children,
}: {
  priority: string;
  className?: string;
  children: React.ReactNode;
}) {
  const borderClass =
    PRIORITY_BORDER_CLASSES[priority] ?? PRIORITY_BORDER_CLASSES.low;
  return (
    <div
      className={cn("border-l-2", borderClass, className)}
      data-testid="priority-border"
    >
      {children}
    </div>
  );
}

/** Small colored dot + agent name. */
export function AgentChip({
  name,
  color,
  className,
}: {
  name: string;
  color: string;
  className?: string;
}) {
  return (
    <span
      className={cn("inline-flex items-center gap-1", className)}
      data-testid="agent-chip"
    >
      <span
        className="shrink-0 rounded-full"
        style={{ width: 5, height: 5, backgroundColor: color }}
        aria-hidden="true"
      />
      <span className="truncate text-muted">{name}</span>
    </span>
  );
}

/**
 * The task's project as a colour dot and name, only while the board shows
 * more than one project; `dotOnly` where a row has no room for the name.
 */
export function TaskProjectChip({
  projectId,
  dotOnly = false,
  className,
}: {
  projectId: string;
  dotOnly?: boolean;
  className?: string;
}) {
  const shows = useBoardShowsProjects();
  const project = useProjectStore((state) =>
    shows ? state.projects.find((item) => item.id === projectId) : undefined,
  );
  if (!project) return null;
  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-1", className)}
      title={dotOnly ? project.name : undefined}
      data-testid="task-project-chip"
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{
          backgroundColor: projectColorForDisplay({
            color: project.color,
            isHome: project.is_home,
          }),
        }}
        aria-hidden="true"
      />
      {dotOnly ? (
        <span className="sr-only">{project.name}</span>
      ) : (
        <span className="truncate text-muted">{project.name}</span>
      )}
    </span>
  );
}

/** Small user icon + user name for user-assigned tasks. */
export function UserChip({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <span
      className={cn("inline-flex items-center gap-1", className)}
      data-testid="user-chip"
    >
      <Icons.User className="h-2.5 w-2.5 text-muted" />
      <span className="truncate text-muted">{name}</span>
    </span>
  );
}

/** Comment icon + count, highlighted green when last comment is from an agent. */
export function CommentBadge({
  count,
  lastAuthor,
  className,
}: {
  count: number;
  lastAuthor?: string | null;
  className?: string;
}) {
  if (count === 0) return null;
  const isAgent = lastAuthor != null && lastAuthor !== "You";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5",
        isAgent ? "text-accent" : "text-muted",
        className,
      )}
      data-testid="comment-badge"
    >
      <Icons.MessageSquare className="h-2.5 w-2.5" />
      <span className="text-appkit-mini">{count}</span>
    </span>
  );
}

/** Calendar icon + relative due date text, red if overdue. Shows repeat icon for recurring. */
export function DueDateChip({
  dueAt,
  isRecurring,
  className,
}: {
  dueAt: string;
  isRecurring?: boolean;
  className?: string;
}) {
  const t = useT(framesTaskboardMessages);
  const now = new Date();
  const due = new Date(dueAt);
  const isOverdue = due.getTime() < now.getTime();

  // "Today", "Tomorrow", "Yesterday", or a short day and month
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86400000);

  let label: string;
  if (diffDays === 0) label = t("today");
  else if (diffDays === 1) label = t("tomorrow");
  else if (diffDays === -1) label = t("yesterday");
  else label = formatDate(due, { day: "numeric", month: "short" });

  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5",
        isOverdue ? "text-danger" : "text-muted",
        className,
      )}
      data-testid="due-date-chip"
    >
      <Icons.Calendar className="h-2.5 w-2.5" />
      <span>{label}</span>
      {isRecurring && <Icons.Repeat2 className="h-2.5 w-2.5" />}
    </span>
  );
}

/** Monospace ticket number display. */
export function TicketChip({
  ticket,
  className,
}: {
  ticket: string;
  className?: string;
}) {
  return (
    <span
      className={cn("font-mono text-muted", className)}
      data-testid="ticket-chip"
    >
      {ticket}
    </span>
  );
}

/** Priority label text (for surfaces without a left border context, e.g. list rows). */
export function PriorityChip({
  priority,
  className,
}: {
  priority: string;
  className?: string;
}) {
  const t = useT(framesTaskboardMessages);
  const textColor = PRIORITY_TEXT_COLORS[priority] ?? PRIORITY_TEXT_COLORS.low;
  const labelKey =
    TASK_PRIORITY_LABEL_KEYS[priority as keyof typeof TASK_PRIORITY_LABEL_KEYS];
  const label = labelKey ? t(labelKey) : priority;
  return (
    <span className={cn(textColor, className)} data-testid="priority-chip">
      {label}
    </span>
  );
}

/** Full metadata row for a task — composable across surfaces. */
export function TaskMetadataRow({
  task,
  showStatus = false,
  showPriority = false,
  className,
}: {
  task: Task;
  /** Show status text (skip on kanban cards where column = status). */
  showStatus?: boolean;
  /** Show priority text (skip on kanban cards where border = priority). */
  showPriority?: boolean;
  className?: string;
}) {
  const t = useT(framesTaskboardMessages);
  const chipSize = "text-appkit-mini";

  return (
    <div
      className={cn("flex items-center gap-2 flex-wrap", className)}
      data-testid="task-metadata-row"
    >
      {task.ticket_display && (
        <TicketChip ticket={task.ticket_display} className={chipSize} />
      )}
      {task.due_at && (
        <DueDateChip
          dueAt={task.due_at}
          isRecurring={task.schedule?.schedule_type === "recurring"}
          className={chipSize}
        />
      )}
      {showStatus && (
        <span className={cn("inline-flex items-center gap-1", chipSize)}>
          <TaskStatusDot status={task.status} size={5} />
          <span className="text-muted">
            {t(TASK_STATUS_LABEL_KEYS[task.status])}
          </span>
        </span>
      )}
      {showPriority && (
        <PriorityChip priority={task.priority} className={chipSize} />
      )}
      {task.assigned_agent_detail && (
        <AgentChip
          name={task.assigned_agent_detail.name}
          color={task.assigned_agent_detail.color}
          className={chipSize}
        />
      )}
      {task.assigned_user_detail && (
        <UserChip name={task.assigned_user_detail.name} className={chipSize} />
      )}
      <CommentBadge
        count={task.comment_count}
        lastAuthor={task.last_comment?.author}
        className={chipSize}
      />
    </div>
  );
}
